import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles,
  Loader2,
  Infinity as InfinityIcon,
  BellRing,
  Zap,
  LineChart,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLANS = [
  {
    key: "cardwatch_pro_monthly",
    label: "Monthly",
    price: "£4.99",
    period: "/mo",
    footnote: "7 days free, then £4.99/mo",
  },
  {
    key: "cardwatch_pro_yearly",
    label: "Yearly",
    price: "£49.99",
    period: "/yr",
    footnote: "7 days free · save 17%",
    highlight: true,
  },
];

const UpgradeDialog = ({ open, onOpenChange, plan }) => {
  const [busy, setBusy] = useState(false);
  const [selectedKey, setSelectedKey] = useState("cardwatch_pro_yearly");

  const startCheckout = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/payments/checkout`,
        {
          lookup_key: selectedKey,
          quantity: 1,
          origin_url: window.location.origin,
        },
        { withCredentials: true }
      );
      const url = res.data?.checkout_url;
      if (!url) {
        throw new Error("No checkout URL returned");
      }
      window.location.assign(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not start Stripe checkout"
      );
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E172C] border border-[#00E676]/30 text-white sm:max-w-lg rounded-2xl overflow-hidden p-4 sm:p-6"
        data-testid="upgrade-dialog"
      >
        <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-[#00E676]/20 blur-3xl" />
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center flex-shrink-0">
              <Sparkles size={17} className="text-[#00E676]" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#00E676] font-bold">
                CardWatch Pro
              </div>
              <DialogTitle
                className="text-white text-lg sm:text-2xl tracking-tight mt-1"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                7 days free — no card required
              </DialogTitle>
              <DialogDescription className="text-slate-300 text-xs sm:text-sm mt-1">
                Free covers{" "}
                <span className="text-white font-semibold">
                  {plan?.card_limit ?? 2} cards
                </span>
                . Try Pro for a week — unlimited cards, Simulator and Optimizer. No card needed to start.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Plan selector */}
        <div className="mt-4 grid grid-cols-2 gap-3 relative" data-testid="upgrade-plans">
          {PLANS.map((p) => {
            const active = p.key === selectedKey;
            return (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelectedKey(p.key)}
                data-testid={`upgrade-plan-${p.label.toLowerCase()}`}
                className={`text-left rounded-xl p-3 sm:p-4 border transition-all ${
                  active
                    ? "border-[#00E676] bg-[#00E676]/[0.08] shadow-[0_10px_30px_-15px_rgba(0,230,118,0.6)]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/30"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`text-[10px] uppercase tracking-[0.22em] font-bold ${
                      active ? "text-[#00E676]" : "text-slate-300"
                    }`}
                  >
                    {p.label}
                  </span>
                  {p.highlight && (
                    <span className="text-[9px] uppercase tracking-widest font-bold text-[#060B19] bg-[#00E676] rounded-full px-1.5 py-0.5">
                      Best
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span
                    className="text-xl sm:text-2xl font-semibold text-white"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {p.price}
                  </span>
                  <span className="text-xs text-slate-300">{p.period}</span>
                </div>
                <div className="mt-1 text-[11px] text-slate-300">{p.footnote}</div>
              </button>
            );
          })}
        </div>

        <ul className="mt-4 space-y-2 sm:space-y-2.5 relative" data-testid="upgrade-benefits">
          {[
            {
              icon: InfinityIcon,
              title: "Unlimited Cards",
              text: "Link every card in your wallet for a true portfolio view.",
            },
            {
              icon: BellRing,
              title: "Zero Late Fees",
              text: "Smart due-date alerts delivered to your phone and email.",
            },
            {
              icon: Zap,
              title: "Auto-Sync",
              text: "Stop typing. Real-time bank updates from major financial institutions.",
            },
            {
              icon: LineChart,
              title: "Credit Simulator",
              text: "Predict your score before you even make a payment.",
            },
          ].map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm text-slate-200"
              data-testid={`upgrade-benefit-${i}`}
            >
              <b.icon size={14} className="text-[#00E676] flex-shrink-0 mt-0.5" />
              <span>
                <span className="text-white font-semibold">{b.title}:</span>{" "}
                <span className="text-slate-300">{b.text}</span>
              </span>
            </li>
          ))}
        </ul>

        <DialogFooter className="pt-3 relative flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="rounded-full border border-white/10 hover:border-white/30 px-5 py-2.5 text-xs text-slate-300 hover:text-white transition-colors"
            data-testid="upgrade-later-button"
          >
            Maybe later
          </button>
          <button
            type="button"
            onClick={startCheckout}
            disabled={busy}
            data-testid="upgrade-confirm-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-6 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60 shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)]"
          >
            {busy && <Loader2 size={13} className="animate-spin" />}
            {busy ? "Redirecting…" : "Start 7-day free trial"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeDialog;
